// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Textarea,
  Badge,
} from '@/components/ui';
import { Loader2 } from 'lucide-react';
import { CadenceField } from './CadenceField';
import type { SchedulerRecord, UpdateSchedulerRequest } from '../api/types/scheduler';

interface EditSchedulerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduler: SchedulerRecord | null;
  onSubmit: (data: UpdateSchedulerRequest) => Promise<void>;
}

export function EditSchedulerDialog({
  open,
  onOpenChange,
  scheduler,
  onSubmit,
}: EditSchedulerDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<UpdateSchedulerRequest>({
    cadence: undefined,
    args: undefined,
  });
  const [argsText, setArgsText] = useState('');
  const [argsError, setArgsError] = useState('');

  // Initialize form when scheduler changes
  useEffect(() => {
    if (scheduler) {
      setFormData({
        cadence: scheduler.cadence,
        args: scheduler.args,
      });
      setArgsText(scheduler.args ? JSON.stringify(scheduler.args, null, 2) : '');
      setArgsError('');
    }
  }, [scheduler]);

  const handleArgsChange = (value: string) => {
    setArgsText(value);
    setArgsError('');

    if (!value.trim()) {
      setFormData((prev) => ({ ...prev, args: null }));
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setArgsError('Arguments must be a JSON object');
        return;
      }
      setFormData((prev) => ({ ...prev, args: parsed }));
    } catch {
      setArgsError('Invalid JSON format');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (argsError) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update scheduler:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!scheduler) {
    return null;
  }

  const getContextInfo = () => {
    const parts = [];
    if (scheduler.org) parts.push(`ORG: ${scheduler.org}`);
    if (scheduler.ws) parts.push(`WS: ${scheduler.ws}`);
    if (scheduler.scope) parts.push(`SCOPE: ${scheduler.scope}`);
    return parts.length > 0 ? parts.join(' / ') : 'Global';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Scheduled Job</DialogTitle>
          <DialogDescription>
            Update the configuration for this scheduled job.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Job Name</Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">
                  {scheduler.job_name}
                </Badge>
              </div>
              <p className="text-sm text-gray-500">
                Job name cannot be changed
              </p>
            </div>

            <div className="grid gap-2">
              <Label>Context</Label>
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {getContextInfo()}
              </div>
              <p className="text-sm text-gray-500">
                Context (org/app/ws) cannot be changed
              </p>
            </div>

            <CadenceField
              value={formData.cadence ?? ''}
              onChange={(cadence) => setFormData((prev) => ({ ...prev, cadence }))}
            />

            <div className="grid gap-2">
              <Label htmlFor="args">Arguments (JSON)</Label>
              <Textarea
                id="args"
                value={argsText}
                onChange={(e) => handleArgsChange(e.target.value)}
                placeholder='{"key": "value"}'
                rows={4}
                className={argsError ? 'border-red-500' : ''}
              />
              {argsError && (
                <p className="text-sm text-red-500">{argsError}</p>
              )}
              <p className="text-sm text-gray-500">
                Optional JSON object with parameters to pass to the job
              </p>
            </div>

            {scheduler.last_run && (
              <div className="grid gap-2">
                <Label>Last Run</Label>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {new Date(scheduler.last_run).toLocaleString()}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !!argsError}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Scheduler
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
